import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import StrUtil from 'util/strUtil';

/**
 * An option in a tree-shaped omnibox filter dropdown (Project or Folder, Type). `filter` is what
 * selecting it applies; `id`/`parentId` give the tree structure. Roots may carry a `section`, which
 * groups consecutive roots under a header (e.g. a Type category).
 */
export interface FilterTreeNode {
  id: string;
  parentId: string | null;
  // Label shown in the tree; can differ from `filter.name` (e.g. a folder whose parent isn't
  // readable is labeled with its path).
  name: string;
  icon: BenchlingIconName;
  // Tile colors for the icon; a neutral gray tile when absent.
  iconColors?: {bgColor: string; color: string};
  filter: OmniboxFilter;
  section?: string;
}

export type FilterTreeRow =
  | {type: 'header'; section: string}
  | {type: 'item'; node: FilterTreeNode; depth: number; hasChildren: boolean};

export interface FilterSearchResult {
  node: FilterTreeNode;
  // Where the match sits, e.g. "Project / Parent", or "Project / ... / Parent" when deeper. Absent
  // for unsectioned top-level items.
  path?: string;
}

function indexTree(nodes: ReadonlyArray<FilterTreeNode>): {
  nodesById: Map<string, FilterTreeNode>;
  childrenByParent: Map<string, Array<FilterTreeNode>>;
  roots: Array<FilterTreeNode>;
} {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const childrenByParent = new Map<string, Array<FilterTreeNode>>();
  const roots: Array<FilterTreeNode> = [];
  for (const node of nodes) {
    if (node.parentId != null && nodesById.has(node.parentId)) {
      const siblings = childrenByParent.get(node.parentId) ?? [];
      siblings.push(node);
      childrenByParent.set(node.parentId, siblings);
    } else {
      roots.push(node);
    }
  }
  return {nodesById, childrenByParent, roots};
}

/**
 * Flattens the tree into the rows currently visible, keeping the order of `nodes`: each root, the
 * children of each expanded node beneath it, and a header wherever the roots' section changes.
 */
export function visibleTreeRows(
  nodes: ReadonlyArray<FilterTreeNode>,
  expandedIds: ReadonlySet<string>
): Array<FilterTreeRow> {
  const {childrenByParent, roots} = indexTree(nodes);
  const rows: Array<FilterTreeRow> = [];
  const walk = (level: ReadonlyArray<FilterTreeNode>, depth: number): void => {
    for (const node of level) {
      const children = childrenByParent.get(node.id) ?? [];
      rows.push({type: 'item', node, depth, hasChildren: children.length > 0});
      if (children.length > 0 && expandedIds.has(node.id)) {
        walk(children, depth + 1);
      }
    }
  };
  let currentSection: string | undefined;
  for (const root of roots) {
    if (root.section != null && root.section !== currentSection) {
      rows.push({type: 'header', section: root.section});
    }
    currentSection = root.section;
    walk([root], 0);
  }
  return rows;
}

function compactPath(segments: ReadonlyArray<string>): string | undefined {
  if (segments.length === 0) {
    return undefined;
  }
  if (segments.length <= 2) {
    return segments.join(' / ');
  }
  return `${segments[0]} / ... / ${segments[segments.length - 1]}`;
}

/**
 * Every option whose own name contains `query` (case-insensitive, as in the sidepanel filters),
 * flattened into one level in tree order so related matches stay together, each labeled with its
 * location: its root's section followed by its ancestors.
 */
export function searchTree(nodes: ReadonlyArray<FilterTreeNode>, query: string): Array<FilterSearchResult> {
  const {nodesById} = indexTree(nodes);
  const everyId = new Set(nodesById.keys());
  return visibleTreeRows(nodes, everyId).flatMap((row): Array<FilterSearchResult> => {
    // Match the option's own name: a tree label can be a path that includes ancestor names.
    if (row.type !== 'item' || !StrUtil.caseInsensitiveContains(row.node.filter.name, query)) {
      return [];
    }
    const ancestors: Array<FilterTreeNode> = [];
    let parent = row.node.parentId != null ? nodesById.get(row.node.parentId) : undefined;
    while (parent != null) {
      ancestors.unshift(parent);
      parent = parent.parentId != null ? nodesById.get(parent.parentId) : undefined;
    }
    const root = ancestors[0] ?? row.node;
    const segments = [...(root.section != null ? [root.section] : []), ...ancestors.map((a) => a.name)];
    return [{node: row.node, path: compactPath(segments)}];
  });
}
